import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import locale from "../../../../constants/locale";
import { BoardColummType, BoardItemCard, UserType } from "../../../../models";
import { useStore } from "../../../../stores";
import { Form, Formik } from "formik";
import * as yup from "yup";
import {
  AntSelect,
  DatePicker,
  Field,
  Loader,
  Modal,
} from "../../../../components";
import { onlyDecimalNumbers } from "../../../../utils/numbers";
import { toast } from "bulma-toast";
import { DealCustomFields } from "../../../contacts/components/SendToBoard";
import { DateTime } from "luxon";

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
};

const UpdateAndDisconnectModal = ({ open, onClose, onUpdate }: Props) => {
  const { t } = useTranslation();
  const { user } = useStore("auth");
  const { selectedBot } = useStore("bots");
  const { users } = useStore("users");
  const [findingDeal, setFindingDeal] = useState(true);
  const [activeDeal, setActiveDeal] = useState<BoardItemCard>();
  const { selectedChat, updateConversationStatus } = useStore("chats");
  const [stageId, setStageId] = useState<number>();
  const {
    boards,
    selectedBoard,
    updateItemToBoard,
    loadBoards,
    selectBoard,
    addItemToBoard,
    findDealByContact,
    createComment,
  } = useStore("boards");

  const conversationStatusEnabled =
    selectedBot?.settings?.conversationStatusConfig?.enabled;
  const conversationStatuses =
    selectedBot?.settings?.conversationStatusConfig?.statuses || [];

  const getModalTitle = () => {
    if (activeDeal && conversationStatusEnabled) {
      return t(locale.updateDealAndStatus);
    }
    if (activeDeal) {
      return t(locale.updateDeal);
    }
    if (conversationStatusEnabled) {
      return t(locale.setConversationStatus);
    }
    return t(locale.updateAndDisconnect);
  };

  useEffect(() => {
    if (selectedChat?.id) {
      const load = async () => {
        try {
          setFindingDeal(true);
          const deal = await findDealByContact(selectedChat?.id);
          setActiveDeal(deal);
          setStageId(deal?.columnId);
        } catch (e) {
          console.log(e);
        } finally {
          setFindingDeal(false);
        }
      };
      load();
    }
  }, [selectedChat?.id, findDealByContact]);

  const isCollaborator = activeDeal && user?.type === UserType.Collaborator;
  const hasClosedLost = useMemo(
    () =>
      selectedBoard?.columns?.find((f) => f.id === stageId)?.type ===
      BoardColummType.ClosedLost,
    [selectedBoard, stageId]
  );

  useEffect(() => {
    if (!selectedBot?.id) {
      return;
    }
    if (boards?.length) {
      return;
    }

    loadBoards();
  }, [loadBoards, selectedBot?.id, boards?.length]);

  useEffect(() => {
    if ((activeDeal as any)?.boardId && selectedBoard?.id !== (activeDeal as any)?.boardId) {
      const board = boards?.find((f) => f.id === (activeDeal as any).boardId);
      if (board) {
        selectBoard(board?.uuid);
      }
    }
  }, [activeDeal, boards, selectBoard, selectedBoard]);

  return (
    <>
      <Formik
        key={activeDeal?.id}
        initialValues={{
          boardId: undefined,
          subscriberId: selectedChat?.id,
          name: "",
          assigneeId: undefined,
          opportunity: "",
          columnId: undefined,
          comment: undefined,
          conversationStatusId: selectedChat?.conversationStatusId,
          ...activeDeal,
          dueDate: activeDeal?.dueDate
            ? activeDeal.dueDate.toJSDate()
            : undefined,
          closedLostReason: activeDeal?.closedLostReason ?? "",
          customFields: activeDeal?.customFields?.map((f: any) => ({
            ...f,
            value:
              f.type === "date" && (f.value as any)?.toDate
                ? (f.value as any)?.toDate()
                : f.value,
          })),
        }}
        validationSchema={yup.object({
          ...(activeDeal && {
            boardId: yup.string().required(t(locale.fieldRequired)),
            name: yup.string().required(t(locale.fieldRequired)),
            columnId: yup.string().required(t(locale.fieldRequired)),
            dueDate: yup.date().required(t(locale.fieldRequired)),
            assigneeId: yup.string().required(t(locale.fieldRequired)),
            opportunity: yup.number().required(t(locale.fieldRequired)),
          }),
          comment: yup.string().optional(),
          ...(conversationStatusEnabled && {
            conversationStatusId: yup
              .string()
              .nullable()
              .required(t(locale.fieldRequired)),
          }),
          ...(hasClosedLost && {
            closedLostReason: yup
              .string()
              .max(30)
              .required(t(locale.fieldRequired)),
          }),
        })}
        onSubmit={async (data) => {
          try {
            // Convert JavaScript Date to Luxon DateTime
            const dueDateLuxon = data.dueDate
              ? DateTime.fromJSDate(data.dueDate as any)
              : undefined;

            if (activeDeal?.id) {
              const deal = await updateItemToBoard(
                {
                  ...activeDeal,
                  ...data,
                  dueDate: dueDateLuxon,
                  opportunity: parseFloat(
                    data.opportunity as unknown as string
                  ),
                  updatedAt: new Date(),
                } as any,
                data.columnId as never,
                activeDeal.columnId as never,
                activeDeal?.assigneeId
              );
              setActiveDeal(deal);
            } else {
              const deal = await addItemToBoard(
                {
                  type: "card",
                  ...data,
                  dueDate: dueDateLuxon,
                  opportunity: parseFloat(
                    data.opportunity as unknown as string
                  ),
                } as any,
                data.columnId as never
              );
              setActiveDeal(deal);
            }
            if (activeDeal && data.comment) {
              await createComment(activeDeal, {
                userId: user?.id,
                comment: data.comment,
              } as any);
            }

            // Update conversation status if enabled and status is selected
            if (conversationStatusEnabled && data.conversationStatusId) {
              await updateConversationStatus(data.conversationStatusId);
            }

            onUpdate();
          } catch (error: any) {
            console.log("error", error);
            let message = t(locale.genericError);
            if (error?.status === 400) {
              message = error.response.data.error;
            }
            toast({
              message,
              position: "top-right",
              dismissible: true,
              type: "is-danger",
              pauseOnHover: true,
            });
          }
        }}
      >
        {({ isSubmitting, submitForm, values, setFieldValue, resetForm }) => (
          <Modal
            isOpen={open}
            title={getModalTitle()}
            onClose={() => {
              resetForm();
              if (onClose) {
                onClose();
              }
            }}
            primaryText={t(locale.updateAndDisconnect)}
            primaryLoading={isSubmitting}
            onPrimary={isSubmitting ? undefined : submitForm}
          >
            <Form>
              {conversationStatusEnabled && (
                <AntSelect
                  size="middle"
                  name="conversationStatusId"
                  showSearch
                  filterOption={(input: any, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  label={t(locale.conversationStatus)}
                  disabled={isSubmitting}
                  options={
                    conversationStatuses?.map((status: any) => ({
                      value: status.id,
                      label: status.name,
                    })) || []
                  }
                />
              )}
              {activeDeal && (
                <>
                  <AntSelect
                    size="middle"
                    name="boardId"
                    showSearch
                    filterOption={(input: any, option: any) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    label={t(locale.sendToPipeline)}
                    allowClear
                    disabled={isCollaborator || Boolean(activeDeal?.id)}
                    onChange={(boardId: any) => {
                      const board = boards?.find((f) => f.id === boardId);
                      if (board) {
                        selectBoard(board?.uuid);
                      }
                    }}
                    options={
                      boards?.map((f) => ({
                        value: f.id,
                        label: f.name,
                      })) || []
                    }
                  />
                  <AntSelect
                size="middle"
                showSearch
                filterOption={(input: any, option: any) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                name="columnId"
                label={t(locale.stage)}
                disabled={isSubmitting}
                onChange={(columnId: any) => {
                  setStageId(columnId);
                }}
                options={
                  selectedBoard?.columns?.map((f) => ({
                    value: f.id,
                    label: f.name,
                  })) || []
                }
              />
              {hasClosedLost && (
                <Field
                  name="closedLostReason"
                  label={t(locale.reason)}
                  placeholder={"Enter " + t(locale.reason)}
                  disabled={isSubmitting}
                  inputProps={{
                    maxLength: 30,
                  }}
                  showCounter
                />
              )}
              <Field
                name="name"
                label={t(locale.name)}
                placeholder={"Enter " + t(locale.name)}
                disabled={isCollaborator || isSubmitting}
              />
              <DatePicker
                name="dueDate"
                label={t(locale.estimatedCloseDate)}
                disabled={isSubmitting}
                minDate={new Date()}
                wrapperClassName="w-100"
                showTimeInput
                dateFormat="MM/dd/yyyy h:mm aa"
              />
              <AntSelect
                size="middle"
                showSearch
                filterOption={(input: any, option: any) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                name="assigneeId"
                label={t(locale.assignee)}
                allowClear
                disabled={isSubmitting}
                options={
                  users?.map((f) => ({
                    value: f.id,
                    label: f.name || f.email,
                  })) || []
                }
              />
              <Field
                name="opportunity"
                label={t(locale.opportunity)}
                placeholder={"Enter " + t(locale.opportunity)}
                disabled={isSubmitting}
                inputProps={{
                  onKeyDown: onlyDecimalNumbers,
                }}
              />
              <DealCustomFields
                value={values.customFields ?? []}
                onChange={(v) => setFieldValue("customFields", v)}
              />
                </>
              )}
              <Field
                name="comment"
                label={t(locale.comments)}
                disabled={isSubmitting}
                useTextArea
                inputProps={{
                  style: { height: 100, resize: "none" },
                }}
              />
            </Form>
            {findingDeal && (
              <div className="loaderClass">
                <Loader />
              </div>
            )}
          </Modal>
        )}
      </Formik>
    </>
  );
};

export default observer(UpdateAndDisconnectModal);
